import { Component, ReactNode, ErrorInfo } from 'react';
import { Stack, Text, Button, Alert, Code, Box } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconBug } from '@tabler/icons-react';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

/**
 * Error Boundary component to catch and handle JavaScript errors
 * Provides graceful error handling and recovery options
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // In production, you might want to log to an error reporting service
    // Example: Sentry.captureException(error, { contexts: { errorInfo } });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box className={styles.errorBoundary}>
          <Stack align="center" gap="lg">
            <Alert
              icon={<IconAlertTriangle size={24} />}
              title="Something went wrong"
              color="red"
              variant="light"
              className={styles.errorAlert}
            >
              <Text size="sm" mb="md">
                An unexpected error occurred while rendering this component.
                You can try refreshing the page or contact support if the problem persists.
              </Text>

              <Stack gap="sm">
                <Button
                  leftSection={<IconRefresh size={16} />}
                  variant="light"
                  color="red"
                  onClick={this.handleRetry}
                  size="sm"
                >
                  Try Again
                </Button>

                <Button
                  variant="outline"
                  color="gray"
                  onClick={this.handleReload}
                  size="sm"
                >
                  Reload Page
                </Button>
              </Stack>
            </Alert>

            {/* Show error details in development */}
            {this.props.showDetails && process.env.NODE_ENV === 'development' && (
              <Alert
                icon={<IconBug size={20} />}
                title="Error Details (Development)"
                color="gray"
                variant="outline"
                className={styles.errorDetails}
              >
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Error Message:
                  </Text>
                  <Code block className={styles.errorCode}>
                    {this.state.error?.message}
                  </Code>

                  {this.state.error?.stack && (
                    <>
                      <Text size="sm" fw={500} mt="md">
                        Stack Trace:
                      </Text>
                      <Code block className={styles.errorCode}>
                        {this.state.error.stack}
                      </Code>
                    </>
                  )}

                  {this.state.errorInfo?.componentStack && (
                    <>
                      <Text size="sm" fw={500} mt="md">
                        Component Stack:
                      </Text>
                      <Code block className={styles.errorCode}>
                        {this.state.errorInfo.componentStack}
                      </Code>
                    </>
                  )}
                </Stack>
              </Alert>
            )}
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Hook-based error boundary for functional components
export interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  return (
    <Box className={styles.errorBoundary}>
      <Alert
        icon={<IconAlertTriangle size={24} />}
        title="Oops! Something went wrong"
        color="red"
        variant="light"
      >
        <Text size="sm" mb="md">
          {error.message || 'An unexpected error occurred.'}
        </Text>
        
        <Button
          leftSection={<IconRefresh size={16} />}
          variant="light"
          color="red"
          onClick={resetError}
          size="sm"
        >
          Try Again
        </Button>
      </Alert>
    </Box>
  );
}